import React, { useState } from 'react';
import { ChevronDown, HelpCircle, Package, CreditCard, Truck, RefreshCw } from 'lucide-react';

const FAQPage: React.FC = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const faqCategories = [
    {
      title: 'Ordering & Payment',
      icon: CreditCard,
      questions: [
        {
          q: 'How do I place an order?',
          a: 'Simply browse our products, add items to your cart, and proceed to checkout. You can pay via credit card, PayPal, or cash on delivery.',
        },
        {
          q: 'What payment methods do you accept?',
          a: 'We accept Visa, MasterCard, PayPal, and cash on delivery for orders within Harare.',
        },
        {
          q: 'Is my payment information secure?',
          a: 'Yes, all payments are processed through secure, encrypted channels. We never store your credit card information.',
        },
      ],
    },
    {
      title: 'Shipping & Delivery',
      icon: Truck,
      questions: [
        {
          q: 'What are your delivery areas?',
          a: 'We currently deliver within Harare metropolitan area. We\'re working to expand to other cities soon.',
        },
        {
          q: 'How long does delivery take?',
          a: 'Orders placed before 2 PM are delivered same day. Orders after 2 PM are delivered the next day.',
        },
        {
          q: 'Do you offer free shipping?',
          a: 'Yes! We offer free shipping on all orders over $50.',
        },
      ],
    },
    {
      title: 'Returns & Refunds',
      icon: RefreshCw,
      questions: [
        {
          q: 'What is your return policy?',
          a: 'We accept returns within 7 days of delivery for unopened, unused products in original packaging.',
        },
        {
          q: 'How do I return an item?',
          a: 'Contact our support team at support@angelcite.com with your order number and reason for return.',
        },
        {
          q: 'How long do refunds take?',
          a: 'Refunds are processed within 5-7 business days after we receive the returned items.',
        },
      ],
    },
    {
      title: 'Products & Quality',
      icon: Package,
      questions: [
        {
          q: 'Are all your products organic?',
          a: 'All products marked as "Organic" are certified organic. We clearly label all organic products.',
        },
        {
          q: 'How fresh are your products?',
          a: 'We source directly from farms and deliver within 24 hours of harvest for maximum freshness.',
        },
        {
          q: 'Do you offer bulk ordering?',
          a: 'Yes, we offer bulk discounts for orders over $200. Contact us for custom quotes.',
        },
      ],
    },
  ];

  return (
    <div className="bg-dark-dark dark:bg-white transition-colors duration-300">
      {/* Hero Section */}
      <section className="relative py-12 md:py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-accent via-accent-light to-primary opacity-90" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <HelpCircle className="w-16 h-16 text-white mx-auto mb-6" />
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              Frequently Asked Questions
            </h1>
            <p className="text-xl text-white/90 max-w-3xl mx-auto">
              Find answers to common questions about our products and services.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20">
        {faqCategories.map((category, categoryIndex) => (
          <div key={categoryIndex} className="mb-12 last:mb-0">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-accent/20 dark:bg-primary/20 rounded-full flex items-center justify-center transition-colors duration-300">
                <category.icon className="w-6 h-6 text-accent dark:text-primary transition-colors duration-300" />
              </div>
              <h2 className="text-2xl font-bold text-white dark:text-dark-dark transition-colors duration-300">
                {category.title}
              </h2>
            </div>

            <div className="space-y-4">
              {category.questions.map((item, index) => {
                const isOpen = openIndex === categoryIndex * 10 + index;
                
                return (
                  <div
                    key={index}
                    className="border border-dark-light dark:border-gray-200 rounded-lg overflow-hidden transition-colors duration-300"
                  >
                    <button
                      className="flex items-center justify-between w-full p-4 text-left bg-dark-light dark:bg-gray-50 hover:bg-dark dark:hover:bg-gray-100 transition-colors duration-300"
                      onClick={() => setOpenIndex(isOpen ? null : categoryIndex * 10 + index)}
                    >
                      <span className="font-medium text-white dark:text-dark-dark transition-colors duration-300">
                        {item.q}
                      </span>
                      <ChevronDown
                        className={`w-5 h-5 text-gray-400 dark:text-gray-500 transition-transform duration-300 ${
                          isOpen ? 'rotate-180' : ''
                        }`}
                      />
                    </button>
                    
                    {isOpen && (
                      <div className="p-4 bg-dark-dark dark:bg-white border-t border-dark-light dark:border-gray-100 transition-colors duration-300">
                        <p className="text-gray-300 dark:text-gray-600 transition-colors duration-300">
                          {item.a}
                        </p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}

        {/* Still Have Questions */}
        <Card className="mt-12 text-center p-8">
          <HelpCircle className="w-12 h-12 text-accent dark:text-primary mx-auto mb-4 transition-colors duration-300" />
          <h3 className="text-xl font-bold text-white dark:text-dark-dark mb-2 transition-colors duration-300">
            Still have questions?
          </h3>
          <p className="text-gray-300 dark:text-gray-600 mb-6 transition-colors duration-300">
            Can't find the answer you're looking for? Our support team is here to help.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button variant="primary" as="a" href="/contact">
              Contact Support
            </Button>
            <Button variant="outline" as="a" href="mailto:support@angelcite.com">
              Email Us
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
};

// Card component for FAQ page (if not imported)
const Card: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = '' }) => (
  <div className={`bg-dark-dark dark:bg-white rounded-xl shadow-md border border-dark-light dark:border-gray-100 p-6 ${className}`}>
    {children}
  </div>
);

// Button component for FAQ page (if not imported)
const Button: React.FC<{ 
  children: React.ReactNode; 
  variant?: 'primary' | 'outline';
  as?: 'a' | 'button';
  href?: string;
  className?: string;
}> = ({ children, variant = 'primary', as: Component = 'button', href, className = '' }) => {
  const baseStyles = 'inline-flex items-center justify-center font-medium rounded-lg px-6 py-3 transition-colors duration-300';
  const variants = {
    primary: 'bg-accent dark:bg-primary text-white hover:bg-accent-dark dark:hover:bg-primary-dark',
    outline: 'border-2 border-accent dark:border-primary text-accent dark:text-primary hover:bg-accent dark:hover:bg-primary hover:text-white',
  };

  return (
    <Component
      href={href}
      className={`${baseStyles} ${variants[variant]} ${className}`}
    >
      {children}
    </Component>
  );
};

export default FAQPage;